import type { ApiClient, ApiRuntime } from '@core/swagger-client';
import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { all } from 'redux-saga/effects';
import { watchLiveWebinar } from './sagas/live-webinar-saga';
import { watchOnDemandWebinar } from './sagas/on-demand-webinar-saga';
import { watchQuestionnaire } from './sagas/questionnaire-saga';
import { LiveWebinarConnection } from './signal-r/live-webinar-connection';
import { OnDemandWebinarConnection } from './signal-r/on-demand-webinar-connection';
import chatLinkPolicyReducer from './slices/chat-link-policy-slice';
import liveWebinarReducer from './slices/live-webinar-slice';
import onDemandWebinarReducer from './slices/on-demand-webinar-slice';
import questionnaireReducer from './slices/questionnaire-slice';

const liveConnection = new LiveWebinarConnection();
const onDemandConnection = new OnDemandWebinarConnection();

// Declared explicitly: redux-saga infers the context type from the initial value, and `api` only
// arrives later through provideStoreRuntime.
interface StoreSagaContext {
    liveConnection: LiveWebinarConnection;
    onDemandConnection: OnDemandWebinarConnection;
    api?: ApiClient;
}

const sagaMiddleware = createSagaMiddleware<StoreSagaContext>({
    context: {
        liveConnection,
        onDemandConnection
    }
});

/**
 * The store is a browser-side singleton: the server renders it but never dispatches to it. Its
 * sagas reach the backend and the SignalR hubs through the app's runtime, handed over here once at
 * startup. Nothing is handed over on the server, so a saga that ever ran there would fail instead
 * of borrowing somebody's credentials.
 */
export function provideStoreRuntime(runtime: ApiRuntime): void {
    sagaMiddleware.setContext({ api: runtime.api });
    liveConnection.hubBaseUrl = runtime.origin;
    onDemandConnection.hubBaseUrl = runtime.origin;
}

export const store = configureStore({
    reducer: {
        chatLinkPolicy: chatLinkPolicyReducer,
        liveWebinar: liveWebinarReducer,
        onDemandWebinar: onDemandWebinarReducer,
        questionnaire: questionnaireReducer
    },
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(sagaMiddleware)
});

function* rootSaga() {
    yield all([watchLiveWebinar(), watchQuestionnaire(), watchOnDemandWebinar()]);
}

sagaMiddleware.run(rootSaga);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
